/**
 * Centralized content config for the ListYourProduct landing page.
 *
 * All user-visible text lives here so that copy changes never require
 * touching component code. Import individual exports in each section.
 */
import { AMAZON_PUBLISHING_ENABLED } from "@/shared/constants/featureFlags"

// ── Brand ──────────────────────────────────────────────────────────────
export const BRAND = {
  name: "ListYourProduct",
  tagline: "AI-Powered Listings",
  supportEmail: "reymishra08@gmail.com",
  whatsappChannelUrl: "https://chat.whatsapp.com/DejLynTdMuiKniTuL3RGgJ?s=hd&p=i&mlu=0",
  socialLinks: {
    github: null,
    twitter: null,
    linkedin: null
  }
}

// ── Navigation ─────────────────────────────────────────────────────────
export const NAV_ITEMS = [
  { label: "How to Use", anchor: "#how-to-use" },
  { label: "Features", anchor: "#features" },
  // { label: "Testimonials", anchor: "#testimonials" },
  { label: "Highlights", anchor: "#highlights" },
  { label: "Pricing", anchor: "#pricing" },
  { label: "FAQ", anchor: "#faq" }
]

// ── How to Use ─────────────────────────────────────────────────────────
export const HOW_TO_USE = {
  heading: "See it in action",
  body: "Watch how easy it is to generate professional product listings in under 2 minutes.",
  videoSrc: "https://www.youtube.com/embed/wVxq-_trXQA"
}

// ── Hero ───────────────────────────────────────────────────────────────
export const HERO = {
  headlineStart: "Create",
  headlineHighlight: "Product Listings",
  body: "Upload a product photo and get a complete, keyword-rich listing — title, description, and bullet points — ready to publish in seconds.",
  emailPlaceholder: "Your email address",
  ctaLabel: "Get started free",
  disclaimerPrefix: 'By clicking "Get started free" you agree to our\u00a0',
  disclaimerLinkLabel: "Terms & Conditions",
  disclaimerLinkHref: "/terms"
}

// ── Logo collection ────────────────────────────────────────────────────
export const LOGO_COLLECTION = {
  heading: "Built for sellers using",
  logos: [
    {
      src: "https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg",
      alt: "Amazon",
      width: 90,
      height: 28
    },
    {
      src: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/33/Meesho_logo.png/250px-Meesho_logo.png",
      alt: "Meesho",
      width: 100,
      height: 32
    }
  ]
}

// ── Features ───────────────────────────────────────────────────────────
export const FEATURES_SECTION = {
  heading: "Everything you need to list faster",
  body: AMAZON_PUBLISHING_ENABLED
    ? "ListYourProduct handles copy, keywords, images, and Amazon listing validation so you can focus on growing your catalogue."
    : "ListYourProduct handles product copy, keywords, and images so you can focus on growing your catalogue."
}

export const FEATURE_ITEMS = [
  {
    title: "AI Listing Generator",
    description:
      "Enter a product name and category (or upload a photo) and receive a complete, marketplace-ready listing in under 5 seconds."
  },
  {
    title: "AI Product Images",
    description:
      "Remove backgrounds and generate polished hero shots that meet marketplace image requirements with one click."
  },
  ...(AMAZON_PUBLISHING_ENABLED
    ? [
        {
          title: "Publish to Amazon",
          description:
            "Connect your Amazon store securely, validate each listing with Amazon, and publish accepted products to your selected marketplace."
        }
      ]
    : [])
]

export const EXTRA_FEATURE_ITEMS = [
  {
    title: "Meesho Profit Calculator",
    iconSrc: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/33/Meesho_logo.png/250px-Meesho_logo.png",
    iconAlt: "Meesho",
    description:
      "Estimate commissions, shipping, and net profit from Meesho orders so you can understand your margins before you scale."
  },
  {
    title: "Amazon Profit Calculator",
    iconSrc: "https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg",
    iconAlt: "Amazon",
    description:
      "Calculate Amazon fees, fulfilment costs, and expected profit before you launch a listing or ad campaign."
  }
]

// ── Testimonials ───────────────────────────────────────────────────────
export const TESTIMONIALS_SECTION = {
  heading: "Loved by sellers",
  body: "Real results from Amazon and Meesho sellers who use ListYourProduct every day."
}

export const TESTIMONIAL_ITEMS = [
  {
    initials: "PS",
    name: "Priya S.",
    occupation: "Amazon Seller",
    testimonial:
      "ListYourProduct cut my listing time from 30 minutes to under 2 minutes per product. The AI-generated titles are spot on for Amazon's search algorithm. My visibility improved noticeably within the first week."
  },
  {
    initials: "RM",
    name: "Rahul M.",
    occupation: "Meesho Reseller",
    testimonial:
      "I was spending hours every day writing product descriptions. With ListYourProduct I just upload a photo and my listing is done. It even generates the right keywords — I've seen a 40% increase in orders."
  },
  {
    initials: "AK",
    name: "Anita K.",
    occupation: "Boutique Owner · Ecommerce Seller",
    testimonial:
      "As a small boutique owner, I couldn't afford a copywriter. ListYourProduct gives me professional-quality listings instantly. The image generation feature is a bonus — my product photos look really polished now."
  },
  {
    initials: "VS",
    name: "Vijay S.",
    occupation: "Electronics Seller",
    testimonial:
      "The keyword suggestions are excellent. My products started appearing in Amazon search results much faster after I switched to ListYourProduct-generated listings."
  },
  {
    initials: "DK",
    name: "Deepa K.",
    occupation: "Fashion Reseller · Meesho",
    testimonial:
      "I manage over 200 SKUs. Before ListYourProduct, keeping listings updated was a nightmare. Now I can refresh an entire category in an afternoon."
  },
  {
    initials: "MR",
    name: "Mohan R.",
    occupation: "Home Goods Seller",
    testimonial:
      "The free trial let me try everything before committing. I bought the 100-credit pack and have been using it ever since — the ROI is obvious."
  }
]

// ── Highlights ─────────────────────────────────────────────────────────
export const HIGHLIGHTS_SECTION = {
  heading: "Why sellers choose ListYourProduct",
  body: "Fast, accurate, and built specifically for Indian marketplace sellers — ListYourProduct removes every friction point between your product and a live listing."
}

export const HIGHLIGHT_ITEMS = [
  {
    title: "Blazing fast",
    description: "Complete listings generated in under 5 seconds — no waiting, no writer's block."
  },
  {
    title: "Marketplace-native",
    description: "Outputs are formatted specifically for Amazon and Meesho — not generic AI content."
  },
  {
    title: "Great for large catalogues",
    description: "Batch your uploads and generate dozens of listings in minutes, not hours."
  },
  {
    title: "Keyword-rich titles",
    description: "Auto-extracted high-volume keywords are woven naturally into every title and description."
  },
  {
    title: "AI product images",
    description: "Studio-quality hero shots with clean backgrounds generated from a single product photo."
  },
  {
    title: "Secure & private",
    description: "Your product data is encrypted and never sold. Only you have access to your listings."
  }
]

// ── Pricing ────────────────────────────────────────────────────────────
export const PRICING_SECTION = {
  heading: "Simple, one-time pricing",
  body: "Start free. Buy image credits whenever you need them — no subscriptions, no recurring charges."
}

// ── FAQ ────────────────────────────────────────────────────────────────
export const FAQ_SECTION = {
  heading: "Frequently asked questions"
}

export const FAQ_ITEMS = [
  {
    question: "Is there a free plan?",
    answer:
      "Yes! Every new account starts with 7 free AI image credits — no credit card required. You can explore all core features before deciding to buy more credits."
  },
  ...(AMAZON_PUBLISHING_ENABLED
    ? [
        {
          question: "How does Amazon publishing work?",
          answer:
            "Sign in, open My Listings, choose your Amazon marketplace, and authorize ListYourProduct in Seller Central. Before publishing, you provide the SKU, Amazon product type, brand, price, inventory, and product identifier or confirm an Amazon-approved identifier exemption. Amazon validates the submission before it is created."
        }
      ]
    : []),
  // {
  //   question: "Which marketplaces are supported?",
  //   answer:
  //     "ListYourProduct generates optimised listings for Amazon and Meesho. Both platforms have distinct requirements, and our AI tailors titles, descriptions, and keywords accordingly."
  // },
  {
    question: "How does the AI generate listings?",
    answer:
      "Enter basic product details (name, category) and optionally upload a product image. Our AI analyses this information and generates SEO-optimised titles, compelling descriptions, and relevant keywords tuned for marketplace search algorithms."
  },
  {
    question: "How do image credits work?",
    answer:
      "Each AI image generated uses 1 credit from your balance. Credits never expire — once purchased they are yours to use at your own pace."
  },
  {
    question: "Is my product data secure?",
    answer:
      "Yes. Your data is stored securely and is never sold to third parties. We use industry-standard encryption and Firebase's security infrastructure. Only you have access to your listings."
  }
]

// ── Footer ─────────────────────────────────────────────────────────────
export const FOOTER = {
  newsletterHeading: "Stay in the loop",
  newsletterBody: "Tips for marketplace sellers, delivered weekly. No spam, ever.",
  emailPlaceholder: "Your email address",
  subscribeLabel: "Subscribe",
  copyright: `© ${new Date().getFullYear()} ListYourProduct. All rights reserved.`,
  columns: {
    product: {
      heading: "Product",
      links: [
        { label: "Features", href: "#features" },
        { label: "Highlights", href: "#highlights" },
        { label: "Pricing", href: "#pricing" },
        { label: "FAQ", href: "#faq" }
      ]
    },
    legal: {
      heading: "Legal",
      links: [
        { label: "Terms of Use", href: "/terms" },
        { label: "Privacy Policy", href: "/privacy" },
        { label: "Contact", href: `mailto:reymishra08@gmail.com` }
      ]
    }
  },
  bottomLinks: [
    { label: "Privacy Policy", href: "/privacy" },
    { label: "Terms of Service", href: "/terms" }
  ]
}
